import { Fragment } from "react";
import { formatMoney } from "@/utils/format";
import type { FicheConfig } from "@/types/fiche";
import type { Cycle, SchoolClass } from "@/types";

interface Props {
  config: FicheConfig;
  classes: SchoolClass[];
  cycles: Cycle[];
  /** Affiche ou masque les dates d'échéance sous chaque montant de tranche. */
  showDueDates?: boolean;
}

interface FeeGroup {
  title: string;
  list: SchoolClass[];
}

export default function FicheDocument({ config, classes, cycles, showDueDates = true }: Props) {
  const primaryCycle: Cycle | undefined = cycles.find((c) => c.code === "PRIMAIRE");
  const secondaryCycle: Cycle | undefined = cycles.find((c) => c.code === "SECONDAIRE");
  const primaryClasses = primaryCycle ? classes.filter((c) => c.cycleId === primaryCycle.id) : [];
  const secondaryClasses = secondaryCycle ? classes.filter((c) => c.cycleId === secondaryCycle.id) : [];
  const keyCycle = [primaryClasses, secondaryClasses];
  const maxTranches = Math.max(
    3,
    ...keyCycle.flatMap((list) =>
      list.map((c) => config.classFiches.find((f) => f.classId === c.id)?.tranches.length ?? 0),
    ),
  );

  const unionDossiers = (list: SchoolClass[]) => {
    const ids = new Set<string>();
    list.forEach((c) => {
      const f = config.classFiches.find((x) => x.classId === c.id);
      f?.dossierIds.forEach((id) => ids.add(id));
    });
    return config.dossiers.filter((d) => ids.has(d.id));
  };

  const primaryDossiers = unionDossiers(primaryClasses);
  const secondaryDossiers = unionDossiers(secondaryClasses);

  const buildRows = (list: SchoolClass[]) =>
    list
      .map((cls) => ({ cls, fiche: config.classFiches.find((f) => f.classId === cls.id) }))
      .filter((r) => r.fiche);

  const groups: FeeGroup[] = [
    { title: "A. Maternelle & Primaire", list: primaryClasses },
    { title: "B. Collège & Lycée", list: secondaryClasses },
  ];

  const colCount = 2 + maxTranches;

  return (
    <div className="print-sheet w-full rounded-lg border border-background-200 bg-white px-5 py-4 text-foreground-900 md:px-7 md:py-5">
      {/* En-tête */}
      <header className="border-b-2 border-foreground-900 pb-2 text-center">
        <h1 className="font-heading text-[15px] font-bold uppercase tracking-wide text-foreground-950">
          {config.header.group}
        </h1>
        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-700">
          {config.header.subtitle}
        </p>
        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-700">
          {config.header.cycles}
        </p>
        <p className="mt-0.5 text-[9px] text-foreground-600">CONTACTS : {config.header.contacts}</p>
        <p className="mt-0.5 text-[9px] font-semibold text-foreground-700">
          ANNÉE SCOLAIRE : {config.header.schoolYear}
        </p>
      </header>

      <div className="my-2.5 text-center">
        <span className="inline-block border-y border-foreground-900 px-5 py-1 font-heading text-[12px] font-bold uppercase tracking-[0.22em] text-foreground-950">
          Fiche de renseignements
        </span>
      </div>

      {/* Dossiers + frais globaux */}
      <section>
        <h3 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-foreground-950">
          Dossiers à fournir
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="rounded border border-foreground-200 bg-background-50/60 px-2.5 py-2">
            <p className="mb-1 text-[9.5px] font-bold uppercase tracking-wide text-primary-700">
              Maternelle &amp; Primaire
            </p>
            <ul className="space-y-0.5 text-[9.5px] leading-tight text-foreground-800">
              {primaryDossiers.map((d) => (
                <li key={d.id} className="flex items-start gap-1.5">
                  <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full bg-foreground-500"></span>
                  {d.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded border border-foreground-200 bg-background-50/60 px-2.5 py-2">
            <p className="mb-1 text-[9.5px] font-bold uppercase tracking-wide text-accent-700">
              Collège &amp; Lycée
            </p>
            <ul className="space-y-0.5 text-[9.5px] leading-tight text-foreground-800">
              {secondaryDossiers.map((d) => (
                <li key={d.id} className="flex items-start gap-1.5">
                  <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full bg-foreground-500"></span>
                  {d.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 rounded border border-foreground-900/70 bg-background-100/70 px-2.5 py-1.5 text-[9.5px] text-foreground-800">
          <span className="font-bold uppercase tracking-wide text-foreground-950">
            Frais d'inscription / réinscription
          </span>
          <span>
            Inscription :{" "}
            <span className="font-bold text-foreground-950">{formatMoney(config.enrollmentFees.inscription)}</span>
          </span>
          <span>
            Réinscription :{" "}
            <span className="font-bold text-foreground-950">
              {formatMoney(config.enrollmentFees.reinscription)}
            </span>
          </span>
          <span className="italic text-foreground-600">(montants uniques, toutes classes confondues)</span>
        </div>
      </section>

      {/* Articles */}
      <section className="mt-2.5">
        <h3 className="mb-1 text-[10px] font-bold uppercase tracking-wide text-foreground-950">
          Articles vendus à l'école
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {config.articles.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1.5 rounded border border-foreground-200 bg-background-50 px-2 py-0.5 text-[9.5px] text-foreground-800"
            >
              {a.label}
              <span className="font-bold text-foreground-950">{formatMoney(a.amount)}</span>
            </span>
          ))}
        </div>
      </section>

      {/* Frais d'écolage — un seul tableau */}
      <section className="mt-2.5">
        <h3 className="mb-1 text-[10px] font-bold uppercase tracking-wide text-foreground-950">
          Frais d'écolage et mode de paiement
        </h3>
        <table className="w-full border-collapse text-[9px]">
          <thead>
            <tr className="bg-background-200/80 text-foreground-800">
              <th className="border border-foreground-300 px-1.5 py-0.5 text-left font-bold">Classe</th>
              {Array.from({ length: maxTranches }).map((_, i) => (
                <th key={i} className="border border-foreground-300 px-1.5 py-0.5 text-right font-bold">
                  Tranche {i + 1}
                </th>
              ))}
              <th className="border border-foreground-300 px-1.5 py-0.5 text-right font-bold">Annuelle</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const rows = buildRows(group.list);
              if (rows.length === 0) return null;
              return (
                <Fragment key={group.title}>
                  <tr className="bg-background-100/70">
                    <td
                      colSpan={colCount}
                      className="border border-foreground-300 px-1.5 py-0.5 text-left text-[8.5px] font-bold uppercase tracking-wide text-foreground-900"
                    >
                      {group.title}
                    </td>
                  </tr>
                  {rows.map(({ cls, fiche }) => {
                    const total = fiche!.tranches.reduce((s, t) => s + t.amount, 0);
                    return (
                      <tr key={cls.id} className="text-foreground-800">
                        <td className="border border-foreground-300 px-1.5 py-0.5 font-semibold text-foreground-950">
                          {cls.name}
                        </td>
                        {Array.from({ length: maxTranches }).map((_, i) => {
                          const tr = fiche!.tranches[i];
                          return (
                            <td
                              key={i}
                              className="border border-foreground-300 px-1.5 py-0.5 text-right tabular-nums"
                            >
                              {tr ? (
                                <>
                                  {formatMoney(tr.amount)}
                                  {showDueDates && (
                                    <span className="block text-[7.5px] leading-tight text-foreground-500">
                                      {tr.dueDate || "—"}
                                    </span>
                                  )}
                                </>
                              ) : (
                                "—"
                              )}
                            </td>
                          );
                        })}
                        <td className="border border-foreground-300 px-1.5 py-0.5 text-right font-bold tabular-nums text-foreground-950">
                          {formatMoney(total)}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Recouvrement + notices */}
      <section className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded border border-foreground-200 bg-background-50/60 px-2.5 py-2">
          <p className="mb-1 text-[9.5px] font-bold uppercase tracking-wide text-foreground-950">
            Rappel : les périodes de recouvrement
          </p>
          <ul className="space-y-0.5 text-[9.5px] leading-tight text-foreground-800">
            {config.periods.map((p) => (
              <li key={p.id}>
                <span className="font-semibold">{p.label}</span> : {p.value}
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-1">
          {config.notices.map((n) => (
            <p key={n} className="text-[9px] italic leading-snug text-foreground-700">
              NB : {n}
            </p>
          ))}
        </div>
      </section>

      {/* Pied */}
      <footer className="mt-4 flex items-end justify-between">
        <p className="text-[10px] font-semibold italic text-foreground-800">{config.closing}</p>
        <div className="text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-800">
            {config.signature}
          </p>
          <div className="mt-5 w-36 border-t border-foreground-400"></div>
        </div>
      </footer>
    </div>
  );
}